import { buildApiUrl } from '../utils/apiConfig';

// Yahoo Finance 2 Service - Frontend wrapper for backend API
class YahooFinanceService {
  constructor() {
    this.name = 'Yahoo Finance 2';
    this.enabled = true; // Enabled by default for all users
  }

  // Get current stock quote using yahoo-finance2 (via backend API)
  async getQuote(symbol) {
    try {
      console.log(`YahooFinance2: Fetching quote for ${symbol}`);
      
      // Call our backend API endpoint that uses yahoo-finance2
      const response = await fetch(buildApiUrl(`/yahoo-quote/${symbol}`));
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data || data.error) {
        console.log(`YahooFinance2: No data found for ${symbol}:`, data);
        return null;
      }
      
      console.log(`YahooFinance2: Successfully fetched data for ${symbol}:`, data);
      return data;
    } catch (error) {
      console.error(`YahooFinance2: Error fetching quote for ${symbol}:`, error);
      return null;
    }
  }

  // Get multiple stock quotes
  async getQuotes(symbols) {
    try {
      console.log(`YahooFinance2: Fetching quotes for ${symbols.length} symbols`);
      
      const promises = symbols.map(symbol => this.getQuote(symbol));
      const results = await Promise.all(promises);
      
      const validResults = results.filter(data => data !== null);
      
      console.log(`YahooFinance2: Successfully fetched ${validResults.length} quotes`);
      return validResults;
    } catch (error) {
      console.error('YahooFinance2: Error fetching multiple quotes:', error);
      return [];
    }
  }

  // Search for stocks by company name (simplified implementation)
  async searchStocks(query) {
    try {
      console.log(`YahooFinance2: Searching for stocks matching "${query}"`);
      
      // For now, return a simple mock search result
      // In a real implementation, you'd use Yahoo Finance's search API
      const mockResults = [
        {
          symbol: query.toUpperCase(),
          name: `${query} Inc.`,
          exchange: 'NASDAQ',
          type: 'Stock'
        }
      ];

      console.log(`YahooFinance2: Found ${mockResults.length} stocks matching "${query}"`);
      return mockResults;
    } catch (error) {
      console.error('YahooFinance2: Error searching stocks:', error);
      return [];
    }
  }

  // Get historical data (simplified implementation)
  async getHistoricalData(symbol, options = {}) {
    try {
      console.log(`YahooFinance2: Fetching historical data for ${symbol}`);
      
      // For now, return empty array
      // In a real implementation, you'd fetch historical data from Yahoo Finance
      console.log(`YahooFinance2: Historical data not implemented yet for ${symbol}`);
      return [];
    } catch (error) {
      console.error(`YahooFinance2: Error fetching historical data for ${symbol}:`, error);
      return [];
    }
  }

  // Check if service is available
  async isAvailable() {
    try {
      // Try to fetch a simple quote to test connectivity
      const testData = await this.getQuote('AAPL');
      return testData !== null;
    } catch (error) {
      console.error('YahooFinance2: Service not available:', error);
      return false;
    }
  }

  // Get mock stock data as fallback
  getMockStockData(symbol) {
    console.log(`YahooFinance2: Generating mock data for ${symbol}`);
    
    // Generate some realistic-looking mock data
    const basePrice = Math.random() * 200 + 50; // Random price between $50-250
    const change = (Math.random() - 0.5) * 10; // Random change between -$5 to +$5
    const changePercent = (change / basePrice) * 100;
    
    return {
      symbol: symbol,
      name: `${symbol} Inc.`,
      price: Math.round(basePrice * 100) / 100,
      change: Math.round(change * 100) / 100,
      changePercent: Math.round(changePercent * 100) / 100,
      open: Math.round((basePrice + (Math.random() - 0.5) * 5) * 100) / 100,
      high: Math.round((basePrice + Math.random() * 5) * 100) / 100,
      low: Math.round((basePrice - Math.random() * 5) * 100) / 100,
      volume: Math.floor(Math.random() * 10000000) + 1000000,
      marketCap: Math.floor(Math.random() * 1000000000000) + 1000000000,
      pe: Math.round((Math.random() * 30 + 10) * 100) / 100,
      dividendYield: Math.round(Math.random() * 5 * 100) / 100,
      previousClose: Math.round((basePrice - change) * 100) / 100,
      currency: 'USD',
      exchange: 'NASDAQ',
      timezone: 'America/New_York',
      lastUpdate: new Date().toISOString(),
      isMockData: true // Flag to indicate this is mock data
    };
  }

  // Get service info
  getServiceInfo() {
    return {
      name: this.name,
      enabled: this.enabled,
      description: 'Real-time stock data from Yahoo Finance using yahoo-finance2',
      features: [
        'Real-time stock quotes',
        'Complete OHLC data',
        'Volume and market cap',
        'PE ratio and dividend yield',
        'Company information',
        'No API key required',
        'High rate limits'
      ],
      limitations: [
        'Requires backend API endpoint',
        'Rate limiting may apply',
        'Data availability depends on Yahoo Finance'
      ]
    };
  }
}

const yahooFinanceService = new YahooFinanceService();
export default yahooFinanceService;